import { useCallback, useMemo } from 'react';
import {
	Checkbox,
	Divider,
	FormControlLabel,
	List,
	ListItemButton,
	ListItemText,
	ListItemIcon,
	ListSubheader,
	Stack,
} from '@mui/material';
import Model from '../models/model';
import consts from '../consts';
import { useUniqueEffect } from '../hooks';

const RoleModulesInput = ({ inputProps: { formik, activeRecord, fieldLabel } }) => {
	const fullPermissions = useMemo(
		() =>
			Array.isArray(formik.values.RoleModules) &&
			formik.values.RoleModules.every((RoleModule) => RoleModule.Permissions === '11111111'),
		[formik.values.RoleModules]
	);

	const partialPermissions = useMemo(
		() =>
			Array.isArray(formik.values.RoleModules) &&
			formik.values.RoleModules.some((RoleModule) => RoleModule.Permissions === '11111111'),
		[formik.values.RoleModules]
	);

	/**
	* Toggles permissions for ancestor modules
	* @param {Object} params - The parameters object
	* @param {number} params.moduleIndex - The index of the current module
	* @param {Array} params.roleModules - The array of role modules
	* @param {Event} params.event - The event object from the checkbox
	*/
	const toogleAnccesstors = ({ moduleIndex, roleModules, event }) => {
		if(!roleModules[moduleIndex].ParentId) {
			return;
		}
		const parentIndex = roleModules.findIndex(({ menuId }) => (menuId === roleModules[moduleIndex].ParentId));
		roleModules[parentIndex].Permissions = event.target.checked ? consts.allPermission : consts.noPermission;
		if(!event.target.checked && !allSiblingsUnchecked(parentIndex)) {
			return;
		}
		toogleAnccesstors({ moduleIndex: parentIndex, roleModules, event});
	}

	/**
	* Handles the toggle event for a module
	* @param {string} Module - The module being toggled
	* @returns {Function} - Event handler function
	*/
	const handleToggle = useCallback(
		(Module) => (event) => {
			const { RoleModules: roleModules = [] } = formik.values || {};
			const moduleIndex = roleModules.findIndex(({ Module: module }) => module === Module);
			roleModules[moduleIndex].Permissions = event.target.checked ? consts.allPermission : consts.noPermission;
			const areSiblingsUnchecked = allSiblingsUnchecked(moduleIndex);
			if(event.target.checked || areSiblingsUnchecked) {
				toogleAnccesstors({ moduleIndex, roleModules, event });
			}
			formik.setFieldValue('RoleModules', roleModules, true);
		},
		[formik.values.RoleModules]
	);

	/**
	* Checks if all sibling modules are unchecked.
	*
	* @param {number} moduleIndex - The index of the current module in the RoleModules array.
	* @returns {boolean} Returns true if all sibling modules are unchecked, false otherwise.
	*/
	const allSiblingsUnchecked = useCallback((moduleIndex) => {
		const { RoleModules: roleModules = [] } = formik.values || {};
		const siblings = roleModules.filter(({ ParentId: parentId }) => parentId === roleModules[moduleIndex].ParentId);
		return siblings.every(({ Permissions }) => !Permissions || Permissions === consts.noPermission);
	}, [formik.values.RoleModules]);

	const handleToggleAll = useCallback(
		(event) => {
			const RoleModules = formik.values.RoleModules.map((RoleModule) => ({
				...RoleModule,
				Permissions: event.target.checked ? consts.allPermission : consts.noPermission
			}));
			formik.setFieldValue('RoleModules', RoleModules, true);
		},
		[formik.values.RoleModules, formik]
	);
	return (
		<List
			subheader={
				<ListSubheader component="div" sx={{ pl: 0, pr: 0 }}>
					<Stack>
						<FormControlLabel
							control={
								<Checkbox
									checked={fullPermissions}
									onChange={handleToggleAll}
									indeterminate={!fullPermissions && partialPermissions}
								/>
							}
							label={fieldLabel}
							sx={{
								color: 'primary.light'
							}}
						/>
						<Divider />
					</Stack>
				</ListSubheader>
			}
			sx={{
				width: '270px',
				'& .MuiListItem-root': {
					padding: 0,
					'& .MuiCheckbox-root': {
						padding: (theme) => theme.spacing(2, 0)
					}
				}
			}}
		>
			{Array.isArray(formik.values.RoleModules) &&
				formik.values.RoleModules.map((RoleModule, i) => (
					<ListItemButton
						key={i}
						role="listitem"
						selected={typeof RoleModule.Permissions === "string" && RoleModule.Permissions[0] === "1"}
						onClick={handleToggle(RoleModule.Module)}
						disabled={consts.ParentMenu.includes(RoleModule.Module)}
						sx={{
							color: '#080808 !important'
						}}
					>
						<ListItemIcon>
							<Checkbox checked={typeof RoleModule.Permissions === "string" && RoleModule.Permissions[0] === "1"} disableRipple /> {/* Disable checkbox for parent menus */}
						</ListItemIcon>
						<ListItemText primary={RoleModule.DisplayName} />
					</ListItemButton>
				))}
		</List>
	);
};
const model = new Model({
	name: 'RoleModule',
	api: 'SecurityRole',
	defaultSort: 'ModifiedOn DESC',
	disableAdd: true,
	disableDelete: true,
	formTitleField: 'Name',
	idProperty: 'RoleId',
	gridTitle: 'RoleModule',
	showGridRefreshBtn: true,
	customizeSaveData: (toSave) => {
		toSave.RoleModules = JSON.stringify(
			JSON.parse(toSave.RoleModules).map(({ ModifiedOn, CreatedOn, ...rest }) => ({
				...rest,
				ModifiedOn: ModifiedOn.replace('$D{', '').replace('}', ''),
				CreatedOn: CreatedOn.replace('$D{', '').replace('}', '')
			}))
		);
		return toSave;
	},
	columns: [
		{ header: 'UniqueId', field: 'RoleId', keyColumn: true, width: 120, hidden: true },
		{ field: 'Name', width: 180, readOnly: true },
		{ field: 'Description', width: 250, required: true },
		{
			field: 'RoleModules',
			header: 'Module',
			excludeOnGrid: true,
			multiple: true,
			inlineLabelSize: 12,
			hideLabel: true,
			customizeInput: RoleModulesInput
		},
		{ header: 'ModifiedBy', field: 'ModifiedbyUser', label: null, width: 155 },
		{ field: 'ModifiedOn', label: null, filterType: 'date', type: 'dateTime', width: 180, isLocal: true }
	]
});

export default model.createFormBase();